// Desk events — the variables of every queued desk mutation. They are also
// the "original event" the Needs-attention tray shows next to the server's
// reason, so they carry the display fields (reference, guest name) the
// receptionist needs to recognise the booking without a network.

export const BALANCE_METHODS = ['card_machine', 'cash', 'eft', 'wallet', 'paylink'] as const;
export type BalanceMethod = (typeof BALANCE_METHODS)[number];

export const BALANCE_METHOD_LABELS: Record<BalanceMethod, string> = {
  card_machine: 'Card machine',
  cash: 'Cash',
  eft: 'EFT',
  wallet: 'Mobile wallet',
  paylink: 'Pay-link',
};

interface DeskEventBase {
  /** Server idempotency key (desk_event_receipts). A replay of the same id
   * returns the original outcome and writes nothing. */
  clientEventId: string;
  businessId: string;
  bookingId: string;
  reference: string;
  guestName: string;
  /** When the receptionist acted (ISO). Sent to the server in `device` so
   * the booking timeline shows both this and the server's sync time. */
  capturedAt: string;
  /** True when the check-in/balance was captured with no connection and
   * only verified against the offline manifest. */
  capturedOffline: boolean;
}

export interface CheckInEvent extends DeskEventBase {
  kind: 'check_in';
  /** The signed QR, when scanned; null for a typed reference. */
  token: string | null;
  /** The typed surname (typed-reference path only). */
  surname: string | null;
}

export interface BalanceRecordEvent extends DeskEventBase {
  kind: 'balance_record';
  amountNad: number;
  method: BalanceMethod;
}

export interface BalanceUndoEvent extends DeskEventBase {
  kind: 'balance_undo';
  undoClientEventId: string;
  amountNad: number;
}

export type DeskEvent = CheckInEvent | BalanceRecordEvent | BalanceUndoEvent;

export const DESK_MUTATION_KEYS = {
  check_in: ['desk', 'check_in'],
  balance_record: ['desk', 'balance_record'],
  balance_undo: ['desk', 'balance_undo'],
} as const;

/** `device` column text for booking_events (≤ 200 chars server-side). */
export function deviceLabel(event: DeskEvent): string {
  return `portal-desk captured_at=${event.capturedAt}${event.capturedOffline ? ' offline' : ''}`;
}

/** Parses a `device` string written by deviceLabel(); null for anything
 * else (the mobile app, older rows). */
export function parseDeviceLabel(device: string | null): { capturedAt: string; offline: boolean } | null {
  if (!device) return null;
  const match = /^portal-desk captured_at=(\S+)( offline)?$/.exec(device);
  if (!match || Number.isNaN(Date.parse(match[1]!))) return null;
  return { capturedAt: match[1]!, offline: match[2] !== undefined };
}

export function eventLabel(event: DeskEvent): string {
  switch (event.kind) {
    case 'check_in':
      return 'Check-in';
    case 'balance_record':
      return `Balance received — N$ ${event.amountNad.toFixed(2)} (${BALANCE_METHOD_LABELS[event.method]})`;
    case 'balance_undo':
      return `Undo balance entry — N$ ${event.amountNad.toFixed(2)}`;
  }
}
